import React, { useRef, useState, useEffect } from 'react';
import { useFullscreen, useToggle, useKey } from 'react-use';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SceneCanvas } from './components/SceneCanvas';
import { SceneView } from './components/SceneView';
import { LoadingScreen } from './components/LoadingScreen';
import { Timeline } from './components/Timeline';
import { SidePanel } from './components/SidePanel';
import { CacheStatus } from './components/CacheStatus';
import { ControlsHint } from './components/ControlsHint';
import { ColorRampLegend } from './components/ColorRampLegend';
import { ErrorToast } from './components/ErrorToast';
import { TransitionToggle } from './components/TransitionToggle';
import { FullscreenToggle } from './components/FullscreenToggle';
import { useFrameNav } from '../application/hooks/useFrameNav';
import { useCache } from '../application/hooks/useCache';
import { useCuboidHover } from '../application/hooks/useCuboidHover';
import { useTransitionConfig } from '../application/hooks/useTransitionConfig';

export function App() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [showFs, toggleFs] = useToggle(false);
  const isFullscreen = useFullscreen(rootRef as React.RefObject<Element>, showFs, { onClose: () => toggleFs(false) });
  useKey('f', () => toggleFs(), {}, [toggleFs]);
  const { currentFrameId, currentFrame, isLoading, error, goTo, next, prev } = useFrameNav();
  const { metrics, loadedFrames, prefetchProgress, prefetchAll } = useCache();
  const { activeCuboid, pinnedCuboid, onUnpin } = useCuboidHover();
  useKey('Escape', () => onUnpin(), {}, [onUnpin]);
  const { mode, setMode } = useTransitionConfig();
  const [controlsVisible, setControlsVisible] = useState(true);

  // Load first frame on mount
  useEffect(() => { goTo(0); }, []);

  // Start background prefetch after first frame loads
  useEffect(() => {
    if (currentFrame && currentFrameId === 0) {
      prefetchAll();
    }
  }, [currentFrame !== null]);

  const showLoading = isLoading && !currentFrame;

  return (
    <div ref={rootRef} className="app-root">
    <ErrorBoundary>
      {showLoading && <LoadingScreen />}
      <SceneCanvas>
        <SceneView />
      </SceneCanvas>

      {/* UI Overlay */}
      <div className="ui-overlay">
        {/* Top-left: cache + controls */}
        <div className="ui-top-left">
          <CacheStatus metrics={metrics} prefetchProgress={prefetchProgress} />
          <ControlsHint
            visible={controlsVisible}
            onDismiss={() => setControlsVisible(false)}
            onShow={() => setControlsVisible(true)}
          />
          <TransitionToggle mode={mode} onToggle={setMode} />
          <FullscreenToggle isFullscreen={isFullscreen} onToggle={toggleFs} />
        </div>

        {/* Top-right: color legend */}
        <div className="ui-top-right">
          <ColorRampLegend min="-3.8m" max="19.0m" />
        </div>

        {/* Bottom-center: timeline */}
        <div className="ui-bottom">
          <Timeline
            frame={currentFrameId}
            onFrameChange={goTo}
            onNext={next}
            onPrev={prev}
            cachedFrames={loadedFrames}
          />
        </div>

        {/* Bottom-center above timeline: side panel */}
        {activeCuboid && (
          <div className="side-panel-anchor">
            <SidePanel cuboid={activeCuboid} pinned={!!pinnedCuboid} onClose={onUnpin} />
          </div>
        )}

        {/* Error toast */}
        {error && (
          <div className="error-toast-anchor">
            <ErrorToast message={error.message} onDismiss={() => {}} />
          </div>
        )}
      </div>
    </ErrorBoundary>
    </div>
  );
}
